/**
 * Class Project Filters
 */
class ProjectFilters {
  async init() {
    await this.checkTarget();
    this.handleFilterOverflowWrapperEvents();
  }

  /**
   * Observes the card list element, if child elements are loaded
   *
   * @param selector
   * @returns {Promise<unknown>}
   */
  async waitForElement(selector) {
    return new Promise((resolve) => {
      if (document.querySelector(selector)) {
        resolve(document.querySelector(selector));
        return;
      }

      const observer = new MutationObserver(() => {
        const el = document.querySelector(selector);
        if (el) {
          observer.disconnect();
          resolve(el);
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    });
  }

  /**
   * Check is target is loaded and do inline style
   *
   * @returns {Promise<void>}
   */
  async checkTarget() {
    const target = await this.waitForElement('.js-mol-project-card');
    this.loading = document.querySelectorAll('.loading:not(#js-org-interactive-map__map-wrapper)');

    if (target) {
      this.loading.forEach((element) => {
        element.classList.remove('loading');
      });

      this.adjustFilters();
    }
  }

  adjustFilters() {
    const cards = Array.from(document.querySelectorAll('.mol--project-card'));
    const filterContainer = document.getElementById('js-project-search-filter-list');
    const allFilters = {};

    /**
     * Create all filters depending on card data-filter attributes with same value > value-count 1
     */
    cards.forEach((card) => {
      Array.from(card.attributes).forEach((attr) => {
        if (attr.name.startsWith('data-filter-project-type')) {
          const key = attr.name;
          const value = attr.value;

          if (!allFilters[key]) {
            allFilters[key] = {};
          }

          if (!value.includes('–')) {
            allFilters[key][value] = (allFilters[key][value] || 0) + 1;
          }
        }
      });
    });

    /**
     * Dynamically fill new filters after "All Projects"- / default-filter
     */
    Object.keys(allFilters).forEach((key) => {
      Object.entries(allFilters[key]).forEach(([text, count]) => {
        if (count > 0) {
          const listElementHtml =
            `<li class="mol--project-search__filter-list-item" data-filter-text="${text}" data-filter-count="${count}" tabindex="0">
                <span class="mol--project-search__filter-list-item__text">${text}</span>
                <span class="mol--project-search__filter-list-item__count">${count}</span>
            </li>`;

          filterContainer.insertAdjacentHTML('beforeend', listElementHtml);
        }
      });
    });

    const filterButtons = Array.from(document.querySelectorAll('.mol--project-search__filter-list-item'));
    const nonAllFilterButtons = filterButtons.filter(b => b.dataset.filter !== 'all');

    function handleToggleFilterButton(btn) {
      /**
       * Handle Button - All Results
       */
      if (btn.dataset.filter === 'all') {
        filterButtons.forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        cards.forEach(card => card.classList.add('is-visible'));
        return;
      }

      /**
       * Toggle between all results and specific filter button
       *
       * @type {Element}
       */
      const allBtn = filterContainer.querySelector('[data-filter="all"]');
      allBtn.classList.remove('is-active');
      btn.classList.toggle('is-active');

      /**
       * Handle active filters by getting their data-filter-text content, to compair later with the card data-filter-* values
       *
       * @type {{key: *, value: *}[]}
       */
      const activeFilters = filterButtons
        .filter(b => b.classList.contains('is-active'))
        .map(b => ({ key: b.dataset.filterText, value: b.dataset.filterValue }));

      /**
       * Results into all cards are active, because no sub-results by active filters are set
       */
      if (activeFilters.length === 0) {
        allBtn.classList.add('is-active');
        cards.forEach(card => card.classList.add('is-visible'));
        return;
      }

      /**
       * Results into only all-projects-filter (allBtn) is set, because all specific filter in a row were selected before
       */
      if (activeFilters.length === nonAllFilterButtons.length) {
        nonAllFilterButtons.forEach(b => b.classList.remove('is-active'));
        allBtn.classList.add('is-active');
        cards.forEach(card => card.classList.add('is-visible'));
        return;
      }

      /**
       * Toggle visibility of cards depending to the active filters by comparing cards filter attr value with filter-key (value name)
       */
      cards.forEach((card) => {
        const visible = activeFilters
          .some(filter => Array.from(card.attributes)
            .some(attr => attr.name.startsWith('data-filter-') && attr.value === filter.key));
        card.classList.toggle('is-visible', visible);
      });
    }

    /**
     * Adjust handler to events
     */
    filterButtons.forEach((btn) => {
      btn.addEventListener('click', () => handleToggleFilterButton(btn));
      btn.addEventListener('keydown', (event) => {
        if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault();
          handleToggleFilterButton(btn);
        }
      });
    });
  }

  /**
   * Adjust fadeout css on top and bottom of the project cards list
   */
  autoAdjustFadeout() {
    this.filterOverflowWrapper = document.getElementById('js-project-search-filter-list-wrapper');
    this.filterScrollContainer = document.getElementById('js-project-search-filter-list');
    const { offsetWidth } = this.filterOverflowWrapper;
    const { scrollWidth } = this.filterScrollContainer;

    if (scrollWidth > offsetWidth) {
      this.filterScrollContainer.classList.add('show-overflow');
    } else {
      this.filterScrollContainer.classList.remove('show-overflow');
    }
  }

  /**
   * Checks if wrapper is resized to adjust fade out css
   */
  handleFilterOverflowWrapperEvents() {
    this.autoAdjustFadeout();
    window.addEventListener('resize', this.autoAdjustFadeout);
  }
}

export default ProjectFilters;

/**
 * Reset filters to default (all Projects)
 */
export function resetFilter() {
  const filterButtons = document.querySelectorAll('.mol--project-search__filter-list-item');

  filterButtons.forEach((btn) => {
    if (!btn.classList.contains('mol--project-search__filter-list-item--default')) {
      btn.classList.remove('is-active');
    } else {
      btn.classList.add('is-active');
    }
  });
}
